import React, { useEffect, useRef, useState } from 'react';
import './Onboarding.css';
import {
  BikeType,
  RidingStyle,
  RidingExperience,
  Flexibility,
  PainPoint,
} from '../constants/bikeConfig';

/* ============================================================================
   Bikendure Fit — multi-step onboarding
   Recreated from the Claude Design handoff (Fit Onboarding.html + app/*.jsx).
   Steps: welcome → bike → ride → body → pain → review → done
   Persistence + resume via localStorage; gating is light (the real gate is
   the report). Finalized appearance: teal accent, dots, glow, warm copy,
   body step included.
   ============================================================================ */

// ── Public form contract (consumed by OnboardingPage) ───────────────────────

export type PainSide = 'left' | 'right' | 'both';

export interface PainEntry {
  side: PainSide;
  note: string;
}

export interface OnboardingFormValues {
  bikeType: BikeType;
  ridingStyle: RidingStyle;
  experience: RidingExperience;
  flexibility: Flexibility;
  /** Mapped to the existing backend enum tokens for backward compatibility. */
  painPoints: PainPoint[];
  // ── Richer fields, persisted locally (backend follow-up) ──
  units: 'metric' | 'imperial';
  height: string;
  weight: string;
  /** Structured pain detail keyed by design pain key. */
  pains: Partial<Record<PainKey, PainEntry>>;
  painFree: boolean;
  painOther: string;
}

interface OnboardingProps {
  onComplete: (values: OnboardingFormValues) => void;
  /** Persists data (backend when signed in, localStorage otherwise). Best-effort. */
  onSave?: (values: OnboardingFormValues) => Promise<void>;
  /** Pre-populate fields when resuming a partial onboarding. */
  initialValues?: Partial<OnboardingFormValues>;
  /** Signed-in identity, shown read-only in the welcome step. */
  identity?: { name?: string; email?: string };
  /** Whether the rider has an authenticated session. Drives the review-step CTA. */
  signedIn?: boolean;
  /**
   * Real signup, run from the review step when the rider is signed out. Creates
   * the account and attaches the collected profile to it. Rejects with a
   * user-facing message, which is surfaced inline on the account card.
   */
  onCreateAccount?: (
    creds: { email: string; password: string },
    values: OnboardingFormValues,
  ) => Promise<void>;
  /**
   * Social sign-up option for the review-step account card. Kept as a render
   * prop so this component stays free of any auth-provider dependency.
   *
   * `persistAnswers` MUST be awaited before the provider redirect — social auth
   * leaves the origin entirely, so answers still living in component state
   * would not survive the round trip. It rejects if the save fails, and the
   * caller is expected to abort the redirect rather than lose the answers.
   */
  socialSignup?: (persistAnswers: () => Promise<void>) => React.ReactNode;
}

// ── Internal model ──────────────────────────────────────────────────────────

type BikeKey = 'road' | 'gravel' | 'mtb' | 'trek' | 'tt';
type PainKey = 'knee' | 'lowback' | 'neck' | 'hands' | 'saddle' | 'foot';

interface Profile {
  bike: BikeKey | null;
  position: RidingStyle | null;
  experience: RidingExperience | null;
  units: 'metric' | 'imperial';
  height: string;
  weight: string;
  flexibility: Flexibility | null;
  pains: Partial<Record<PainKey, PainEntry>>;
  painFree: boolean;
  painOther: string;
}

const DEFAULT_PROFILE: Profile = {
  bike: null,
  position: null,
  experience: null,
  units: 'metric',
  height: '',
  weight: '',
  flexibility: null,
  pains: {},
  painFree: false,
  painOther: '',
};

const RESUME_KEY = 'bikendure_onboarding_progress';

// ── Static data ──────────────────────────────────────────────────────────────

const BIKES: Array<{ k: BikeKey; img: string; name: string }> = [
  { k: 'road',   img: '/images/onboarding/bike-road.png',   name: 'Road' },
  { k: 'gravel', img: '/images/onboarding/bike-gravel.png', name: 'Gravel' },
  { k: 'mtb',    img: '/images/onboarding/bike-mtb.png',    name: 'Mountain' },
  { k: 'trek',   img: '/images/onboarding/bike-trek.png',   name: 'Trekking · City' },
  { k: 'tt',     img: '/images/onboarding/bike-tt.png',     name: 'TT · Tri' },
];

const POSITIONS: Array<{ k: RidingStyle; name: string; sub: string }> = [
  { k: 'comfort',     name: 'Comfort',     sub: 'Upright, easy on the back' },
  { k: 'balanced',    name: 'Balanced',    sub: 'A bit of both' },
  { k: 'performance', name: 'Performance', sub: 'Low and aero' },
];

const EXPERIENCE: Array<{ k: RidingExperience; name: string; sub: string }> = [
  { k: 'beginner',     name: 'New to it',   sub: 'Under a year' },
  { k: 'intermediate', name: 'Comfortable', sub: '1–3 years' },
  { k: 'experienced',  name: 'Experienced', sub: '3+ years' },
];

const FLEX: Array<{ k: Flexibility; img: string; name: string; desc: string }> = [
  { k: 'limited', img: '/images/onboarding/flex-limited.png', name: 'Limited', desc: 'Hands reach mid-shin' },
  { k: 'average', img: '/images/onboarding/flex-average.png', name: 'Average', desc: 'Hands reach your ankles' },
  { k: 'good',    img: '/images/onboarding/flex-good.png',    name: 'Supple',  desc: 'Hands reach the floor' },
];

// refer:true flags an item worth a hands-on assessment note
const PAINS: Array<{ k: PainKey; name: string; hint?: string; refer?: boolean; noSide?: boolean }> = [
  { k: 'knee',   name: 'Knee pain',                hint: 'front, back or side' },
  { k: 'lowback', name: 'Lower back pain',         hint: 'aches after a while', refer: true, noSide: true },
  { k: 'neck',   name: 'Neck or shoulder tension', noSide: true },
  { k: 'hands',  name: 'Hand or wrist numbness',   hint: 'pins & needles' },
  { k: 'saddle', name: 'Saddle discomfort',        hint: 'sit-bones or soft tissue', noSide: true },
  { k: 'foot',   name: 'Foot pain or hot spots',   hint: 'numbness, burning' },
];

const SIDES: Array<{ k: PainSide; label: string }> = [
  { k: 'left', label: 'Left' },
  { k: 'right', label: 'Right' },
  { k: 'both', label: 'Both' },
];

// ── Mapping internal ⇄ public ─────────────────────────────────────────────────

const BIKE_TO_TYPE: Record<BikeKey, BikeType> = {
  road: 'road', gravel: 'gravel', mtb: 'mountain', trek: 'trekking', tt: 'triathlon',
};
const TYPE_TO_BIKE: Record<BikeType, BikeKey> = {
  road: 'road', gravel: 'gravel', mountain: 'mtb', trekking: 'trek', city: 'trek', triathlon: 'tt',
};
const PAIN_TO_POINT: Partial<Record<PainKey, PainPoint>> = {
  knee: 'knee_both', lowback: 'lower_back', neck: 'neck_shoulder', hands: 'hand_wrist', saddle: 'saddle',
};
const POINT_TO_PAIN: Partial<Record<PainPoint, PainKey>> = {
  knee_front: 'knee', knee_back: 'knee', knee_both: 'knee',
  lower_back: 'lowback', neck_shoulder: 'neck', hand_wrist: 'hands', saddle: 'saddle',
};

function painAnswered(p: Profile): boolean {
  return p.painFree || Object.keys(p.pains).length > 0 || p.painOther.trim().length > 0;
}

function buildValues(p: Profile): OnboardingFormValues {
  const points: PainPoint[] = [];
  (Object.keys(p.pains) as PainKey[]).forEach((k) => {
    const point = PAIN_TO_POINT[k];
    if (point && !points.includes(point)) points.push(point);
  });
  const painPoints: PainPoint[] = points.length ? points : ['none'];

  return {
    bikeType:    BIKE_TO_TYPE[p.bike ?? 'road'],
    ridingStyle: p.position ?? 'balanced',
    experience:  p.experience ?? 'intermediate',
    flexibility: p.flexibility ?? 'average',
    painPoints,
    units: p.units,
    height: p.height,
    weight: p.weight,
    pains: p.pains,
    painFree: p.painFree,
    painOther: p.painOther,
  };
}

function seedFromValues(v?: Partial<OnboardingFormValues>): Profile {
  if (!v) return { ...DEFAULT_PROFILE };

  // Prefer the richer structured pains when present, else derive from painPoints.
  const pains: Partial<Record<PainKey, PainEntry>> = v.pains ? { ...v.pains } : {};
  let painFree = v.painFree ?? false;
  if (!v.pains && v.painPoints) {
    for (const pt of v.painPoints) {
      if (pt === 'none') { painFree = true; continue; }
      const key = POINT_TO_PAIN[pt];
      if (key && !pains[key]) pains[key] = { side: 'both', note: '' };
    }
  }

  return {
    bike:        v.bikeType ? TYPE_TO_BIKE[v.bikeType] : null,
    position:    v.ridingStyle ?? null,
    experience:  v.experience ?? null,
    units:       v.units ?? 'metric',
    height:      v.height ?? '',
    weight:      v.weight ?? '',
    flexibility: v.flexibility ?? null,
    pains,
    painFree,
    painOther:   v.painOther ?? '',
  };
}

// ── Icons (Lucide-style, stroke 1.75) ─────────────────────────────────────────

type IconProps = { width?: number; height?: number };
const mk = (children: React.ReactNode, defaultSize = 20) =>
  ({ width = defaultSize, height = defaultSize }: IconProps) => (
    <svg width={width} height={height} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  );

const Icons = {
  Check:   mk(<path d="M20 6 9 17l-5-5" />),
  ChevR:   mk(<path d="m9 18 6-6-6-6" />),
  ChevL:   mk(<path d="m15 18-6-6 6-6" />),
  User:    mk(<><circle cx="12" cy="8" r="4" /><path d="M4 20c0-3.5 3.6-6 8-6s8 2.5 8 6" /></>),
  Mail:    mk(<><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>),
  Lock:    mk(<><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></>, 14),
  Shield:  mk(<path d="M12 3 5 6v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6l-7-3z" />),
  Info:    mk(<><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></>, 16),
  Flag:    mk(<><path d="M5 21V5a1 1 0 0 1 1-1h10l-2 4 2 4H6" /><path d="M5 4v17" /></>, 16),
  Refresh: mk(<><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M3 21v-5h5" /></>, 18),
};

// ── Step components ────────────────────────────────────────────────────────────

type SetFn = <K extends keyof Profile>(k: K, v: Profile[K]) => void;

function WelcomeStep({ identity }: { identity?: OnboardingProps['identity'] }) {
  const firstName = (identity?.name || '').split(' ')[0];
  const hasIdentity = !!(identity?.name || identity?.email);
  return (
    <div className="ob-step-enter">
      <span className="ob-kicker">Bikendure Fit · Profile setup</span>
      <h1 className="ob-title">
        {firstName
          ? `Quick check, ${firstName} — this is the profile we'll build your fit around.`
          : "Let's set up your fit profile."}
      </h1>
      <p className="ob-sub">
        {hasIdentity
          ? "We've pulled your details from sign-up. From here it's a handful of quick questions about you and your bike — no studio, no markers. Everything saves as you go."
          : "A handful of quick questions about you and your bike — no studio, no markers. Everything saves as you go, so you can stop and pick up anytime."}
      </p>

      {hasIdentity ? (
        <div className="ob-identity">
          {identity?.name ? (
            <div className="ob-id-row">
              <div className="ob-id-ic"><Icons.User /></div>
              <div className="ob-id-meta">
                <span className="ob-id-k">Name</span>
                <span className="ob-id-v">{identity.name}</span>
              </div>
              <span className="ob-id-lock"><Icons.Lock /> From sign-up</span>
            </div>
          ) : null}
          {identity?.email ? (
            <div className="ob-id-row">
              <div className="ob-id-ic"><Icons.Mail /></div>
              <div className="ob-id-meta">
                <span className="ob-id-k">Email</span>
                <span className="ob-id-v">{identity.email}</span>
              </div>
              <span className="ob-id-lock"><Icons.Lock /> From sign-up</span>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="ob-reassure">
        <Icons.Shield />
        <p>This stays yours. Nothing here is medical, you can change any answer later, and your fit videos are processed privately — <b>we never store the footage</b>.</p>
      </div>
    </div>
  );
}

function BikeStep({ profile, set }: { profile: Profile; set: SetFn }) {
  return (
    <div className="ob-step-enter">
      <span className="ob-kicker">Step 1 · Your bike</span>
      <h1 className="ob-title">Which bike are we fitting?</h1>
      <p className="ob-sub">Each type has its own ideal joint-angle ranges — a TT bike isn't meant to sit you like a tourer.</p>
      <div className="ob-step-body">
        <div className="ob-bike-grid">
          {BIKES.map((b) => {
            const on = profile.bike === b.k;
            return (
              <button key={b.k} type="button" className={'ob-bike' + (on ? ' is-active' : '')}
                      onClick={() => set('bike', b.k)}>
                <span className="ob-check"><Icons.Check width={11} height={11} /></span>
                <span className="ob-bike-media">
                  <img src={b.img} alt="" loading="lazy" />
                </span>
                <span className="ob-bike-name">{b.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Segmented<T extends string>({ options, value, onChange }: {
  options: Array<{ k: T; name: string; sub: string }>;
  value: T | null;
  onChange: (k: T) => void;
}) {
  return (
    <div className="ob-seg">
      {options.map((o) => (
        <button key={o.k} type="button" className={'ob-seg-opt' + (value === o.k ? ' is-active' : '')}
                onClick={() => onChange(o.k)}>
          <span className="ob-seg-name">{o.name}</span>
          <span className="ob-seg-sub">{o.sub}</span>
        </button>
      ))}
    </div>
  );
}

function RideStep({ profile, set }: { profile: Profile; set: SetFn }) {
  return (
    <div className="ob-step-enter">
      <span className="ob-kicker">Step 2 · How you ride</span>
      <h1 className="ob-title">How do you like to sit on the bike?</h1>
      <p className="ob-sub">Your preferred position and experience set the comfort-versus-aero target for your fit.</p>
      <div className="ob-step-body">
        <div className="ob-group">
          <h3 className="ob-group-label">Riding position</h3>
          <p className="ob-group-sub">How aggressive a posture do you actually want to hold?</p>
          <Segmented options={POSITIONS} value={profile.position} onChange={(v) => set('position', v)} />
        </div>
        <div className="ob-group">
          <h3 className="ob-group-label">Experience</h3>
          <p className="ob-group-sub">Roughly how long have you been riding regularly?</p>
          <Segmented options={EXPERIENCE} value={profile.experience} onChange={(v) => set('experience', v)} />
        </div>
      </div>
    </div>
  );
}

function BodyStep({ profile, set }: { profile: Profile; set: SetFn }) {
  const metric = profile.units !== 'imperial';
  return (
    <div className="ob-step-enter">
      <span className="ob-kicker">Step 3 · About your body</span>
      <h1 className="ob-title">A couple of optional details.</h1>
      <p className="ob-sub">The video measures your body directly, so these are just a sanity check — skip anything you'd rather not share.</p>
      <div className="ob-step-body">
        <div className="ob-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 className="ob-group-label" style={{ margin: 0 }}>Height &amp; weight</h3>
            <div className="ob-unit-toggle">
              <button type="button" className={metric ? 'is-active' : ''} onClick={() => set('units', 'metric')}>cm · kg</button>
              <button type="button" className={!metric ? 'is-active' : ''} onClick={() => set('units', 'imperial')}>ft · lb</button>
            </div>
          </div>
          <div className="ob-fields">
            <div className="ob-field">
              <div className="ob-field-label"><span className="l">Height</span><span className="opt">Optional</span></div>
              <div className="ob-input-wrap">
                <input type="text" inputMode="decimal" placeholder={metric ? '172' : "5'8\""}
                       value={profile.height} onChange={(e) => set('height', e.target.value)} />
                <span className="ob-input-unit">{metric ? 'cm' : ''}</span>
              </div>
            </div>
            <div className="ob-field">
              <div className="ob-field-label"><span className="l">Weight</span><span className="opt">Optional</span></div>
              <div className="ob-input-wrap">
                <input type="text" inputMode="decimal" placeholder={metric ? '68' : '150'}
                       value={profile.weight} onChange={(e) => set('weight', e.target.value)} />
                <span className="ob-input-unit">{metric ? 'kg' : 'lb'}</span>
              </div>
            </div>
          </div>
          <p className="ob-why">Weight just helps us read saddle and contact-pressure notes a little better. It never appears in your report and you can leave it blank.</p>
        </div>

        <div className="ob-group">
          <h3 className="ob-group-label">Flexibility</h3>
          <p className="ob-group-sub">Stand tall, legs together, and reach for your toes — how far do your hands get?</p>
          <div className="ob-flex-grid">
            {FLEX.map((f) => {
              const on = profile.flexibility === f.k;
              return (
                <button key={f.k} type="button" className={'ob-flex' + (on ? ' is-active' : '')}
                        onClick={() => set('flexibility', f.k)}>
                  <span className="ob-check"><Icons.Check width={11} height={11} /></span>
                  <span className="ob-flex-media"><img src={f.img} alt="" loading="lazy" /></span>
                  <span className="ob-flex-name">{f.name}</span>
                  <span className="ob-flex-desc">{f.desc}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function PainRow({ p, state, onToggle, onSide, onNote }: {
  p: typeof PAINS[number];
  state?: PainEntry;
  onToggle: () => void;
  onSide: (s: PainSide) => void;
  onNote: (n: string) => void;
}) {
  const on = !!state;
  return (
    <div className={'ob-pain' + (on ? ' is-active' : '')}>
      <div className="ob-pain-head" onClick={onToggle} role="button" tabIndex={0}
           onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}>
        <span className="ob-pain-box"><Icons.Check width={12} height={12} /></span>
        <span className="ob-pain-name">{p.name}</span>
        {p.hint && !on ? <span className="ob-pain-hint">{p.hint}</span> : null}
      </div>
      {on && state ? (
        <div className="ob-pain-detail">
          {!p.noSide ? (
            <div>
              <div className="ob-side-label">Which side?</div>
              <div className="ob-side">
                {SIDES.map((s) => (
                  <button key={s.k} type="button" className={state.side === s.k ? 'is-active' : ''}
                          onClick={() => onSide(s.k)}>{s.label}</button>
                ))}
              </div>
            </div>
          ) : null}
          <textarea className="ob-note"
                    placeholder="Add a detail if you like — e.g. 'sharp on the outside of the right knee after 30 min'"
                    value={state.note} onChange={(e) => onNote(e.target.value)} />
          {p.refer && state.note.length > 24 ? (
            <div className="ob-refer"><Icons.Flag />
              <span>Thanks for the detail — this is the kind of thing we'll flag for a hands-on look if the video can't fully resolve it.</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function PainStep({ profile, set }: { profile: Profile; set: SetFn }) {
  const pains = profile.pains;
  const painFree = profile.painFree;

  const togglePain = (k: PainKey) => {
    const next = { ...pains };
    if (next[k]) delete next[k];
    else next[k] = { side: 'both', note: '' };
    set('pains', next);
    if (Object.keys(next).length) set('painFree', false);
  };
  const setSide = (k: PainKey, side: PainSide) =>
    set('pains', { ...pains, [k]: { side, note: pains[k]?.note ?? '' } });
  const setNote = (k: PainKey, note: string) =>
    set('pains', { ...pains, [k]: { side: pains[k]?.side ?? 'both', note } });
  const choosePainFree = () => {
    const nextFree = !painFree;
    set('painFree', nextFree);
    if (nextFree) { set('pains', {}); set('painOther', ''); }
  };

  return (
    <div className="ob-step-enter">
      <span className="ob-kicker">Step 4 · Pain &amp; injuries</span>
      <h1 className="ob-title">Anything bothering you on the bike?</h1>
      <p className="ob-sub">This is the most useful thing you can tell us — it tells the analysis exactly which joints to scrutinise. Tap anything that sounds familiar and let us know which side.</p>
      <div className="ob-step-body" style={{ gap: 16 }}>
        <div className={'ob-pain is-painfree' + (painFree ? ' is-active' : '')}>
          <div className="ob-pain-head" onClick={choosePainFree} role="button" tabIndex={0}
               onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choosePainFree(); } }}>
            <span className="ob-pain-box"><Icons.Check width={12} height={12} /></span>
            <span className="ob-pain-name">I'm riding pain-free right now</span>
          </div>
        </div>

        <div className="ob-pain-or">or tell us where</div>

        <div className="ob-pain-list">
          {PAINS.map((p) => (
            <PainRow key={p.k} p={p} state={pains[p.k]}
                     onToggle={() => togglePain(p.k)}
                     onSide={(s) => setSide(p.k, s)}
                     onNote={(n) => setNote(p.k, n)} />
          ))}
        </div>

        <div className="ob-group" style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 22 }}>
          <h3 className="ob-group-label">Anything else?</h3>
          <p className="ob-group-sub">Even "left foot goes numb after 30 minutes" helps. There's no wrong answer here.</p>
          <textarea className="ob-note" style={{ minHeight: 64 }}
                    placeholder="Tell us in your own words…"
                    value={profile.painOther}
                    onChange={(e) => { set('painOther', e.target.value); if (e.target.value) set('painFree', false); }} />
        </div>
      </div>
    </div>
  );
}

// ── Review / summary ──────────────────────────────────────────────────────────

const sideLabel: Record<PainSide, string> = { left: 'L', right: 'R', both: 'L+R' };

/** Inline validation for the review-step account card. */
export interface SignupErrors { email?: string; password?: string; form?: string }

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PASSWORD = 8;

function validateSignup(email: string, password: string): SignupErrors {
  const errors: SignupErrors = {};
  const e = email.trim();
  if (!e) errors.email = 'Enter your email address.';
  else if (!EMAIL_RE.test(e)) errors.email = "That doesn't look like a valid email address.";
  if (!password) errors.password = 'Choose a password.';
  else if (password.length < MIN_PASSWORD) errors.password = `Use at least ${MIN_PASSWORD} characters.`;
  return errors;
}

/** Controlled state for the account card, owned by the main component so the
 *  nav CTA can validate and submit it. */
interface SignupBinding {
  email: string;
  password: string;
  errors: SignupErrors;
  submitting: boolean;
  onEmail: (v: string) => void;
  onPassword: (v: string) => void;
  /** Optional social sign-up control, rendered above the email/password fields. */
  social: React.ReactNode;
}

function SignupCard({ signup }: { signup: SignupBinding }) {
  const { email, password, errors, submitting, onEmail, onPassword, social } = signup;
  return (
    <div className="ob-signup">
      <h4>Save this to a profile</h4>
      <p className="ob-signup-body">
        Create an account and you'll never type any of this again — your bike, riding style
        and pain history come back next time. It stays here on Bikendure: never shared, never sold.
      </p>

      {social ? (
        <>
          <div className="ob-signup-social">{social}</div>
          <div className="ob-signup-or"><span>or</span></div>
        </>
      ) : null}

      <div className="ob-fields ob-signup-fields">
        <div className="ob-field">
          <label className="ob-field-label" htmlFor="ob-signup-email">
            <span className="l">Email</span>
          </label>
          <div className={'ob-input-wrap' + (errors.email ? ' has-error' : '')}>
            <input
              id="ob-signup-email"
              type="email"
              className="ob-signup-input"
              value={email}
              onChange={(e) => onEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              disabled={submitting}
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'ob-signup-email-err' : undefined}
            />
          </div>
          {errors.email ? <p className="ob-field-error" id="ob-signup-email-err">{errors.email}</p> : null}
        </div>

        <div className="ob-field">
          <label className="ob-field-label" htmlFor="ob-signup-password">
            <span className="l">Password</span>
            <span className="opt">{MIN_PASSWORD}+ CHARS</span>
          </label>
          <div className={'ob-input-wrap' + (errors.password ? ' has-error' : '')}>
            <input
              id="ob-signup-password"
              type="password"
              className="ob-signup-input"
              value={password}
              onChange={(e) => onPassword(e.target.value)}
              autoComplete="new-password"
              minLength={MIN_PASSWORD}
              disabled={submitting}
              aria-invalid={!!errors.password}
              aria-describedby={errors.password ? 'ob-signup-password-err' : undefined}
            />
          </div>
          {errors.password ? <p className="ob-field-error" id="ob-signup-password-err">{errors.password}</p> : null}
        </div>
      </div>

      {errors.form ? <p className="ob-field-error ob-signup-form-error" role="alert">{errors.form}</p> : null}

      <p className="ob-signup-fine">
        <Icons.Lock width={14} height={14} />
        Your answers and your footage stay on Bikendure. No third parties, no resale, delete anytime.
      </p>
    </div>
  );
}

function SummaryRow({ k, children, onEdit }: { k: string; children: React.ReactNode; onEdit?: () => void }) {
  return (
    <div className="ob-sum-row">
      <span className="ob-sum-k">{k}</span>
      <span className="ob-sum-v">{children}</span>
      {onEdit ? <button type="button" className="ob-sum-edit" onClick={onEdit}>Edit</button> : null}
    </div>
  );
}

function SummaryStep({ profile, goTo, signedIn, signup }: {
  profile: Profile;
  goTo: (i: number) => void;
  /** Signed out gets the account-creation card instead of the confirmation panel. */
  signedIn: boolean;
  signup: SignupBinding | null;
}) {
  const metric = profile.units !== 'imperial';
  const ok = painAnswered(profile);
  const hasBody = !!(profile.height || profile.weight);

  const tags: Array<{ label: string; side: PainSide | null }> = [];
  if (profile.painFree) tags.push({ label: 'Pain-free', side: null });
  (Object.keys(profile.pains) as PainKey[]).forEach((k) => {
    const p = PAINS.find((x) => x.k === k);
    if (p) tags.push({ label: p.name, side: p.noSide ? null : (profile.pains[k]?.side ?? null) });
  });

  const referFlag =
    (Object.keys(profile.pains) as PainKey[]).some((k) => {
      const p = PAINS.find((x) => x.k === k);
      return !!p?.refer && (profile.pains[k]?.note.length ?? 0) > 24;
    }) || profile.painOther.length > 40;

  const bikeName = BIKES.find((b) => b.k === profile.bike)?.name ?? '—';
  const posName = POSITIONS.find((o) => o.k === profile.position)?.name ?? '—';
  const expName = EXPERIENCE.find((o) => o.k === profile.experience)?.name ?? '—';
  const flexName = FLEX.find((f) => f.k === profile.flexibility)?.name ?? '—';

  return (
    <div className="ob-step-enter">
      <span className="ob-kicker">Step 5 · Review</span>
      <h1 className="ob-title">Here's what we'll build your fit around.</h1>
      <p className="ob-sub">Have a quick look — tweak anything that's off. This all lands in your profile and you can change it later.</p>

      <div className="ob-summary">
        <SummaryRow k="Bike" onEdit={() => goTo(1)}>{bikeName}</SummaryRow>
        <SummaryRow k="Riding" onEdit={() => goTo(2)}>{posName} · {expName}</SummaryRow>
        <SummaryRow k="Body" onEdit={() => goTo(3)}>
          {hasBody ? (
            <span>
              {profile.height ? `${profile.height} ${metric ? 'cm' : ''}` : ''}
              {profile.height && profile.weight ? ' · ' : ''}
              {profile.weight ? `${profile.weight} ${metric ? 'kg' : 'lb'}` : ''}
              {'  ·  '}{flexName} flexibility
            </span>
          ) : (
            <span><span className="muted">Skipped</span>{'  ·  '}{flexName} flexibility</span>
          )}
        </SummaryRow>
        <SummaryRow k="Pain & injuries" onEdit={() => goTo(4)}>
          {tags.length ? (
            <span className="ob-sum-tags">
              {tags.map((t, i) => (
                <span key={i} className="ob-tag">{t.label}{t.side ? <span className="side">{sideLabel[t.side]}</span> : null}</span>
              ))}
            </span>
          ) : <span className="muted">Not answered yet</span>}
        </SummaryRow>
        {profile.painOther.trim() ? (
          <SummaryRow k="In your words" onEdit={() => goTo(4)}>
            <span style={{ fontStyle: 'italic', color: 'var(--bk-fg-2)' }}>“{profile.painOther.trim()}”</span>
          </SummaryRow>
        ) : null}
      </div>

      {referFlag ? (
        <div className="ob-reassure" style={{ borderColor: 'rgba(245,158,11,0.25)', background: 'rgba(245,158,11,0.06)' }}>
          <span style={{ color: 'var(--bk-amber)' }}><Icons.Flag /></span>
          <p>What you described may be worth a hands-on look beyond what a phone video resolves. We'll surface that option with your report — no pressure.</p>
        </div>
      ) : null}

      {!ok ? (
        <div className="ob-gate block">
          <Icons.Info />
          <div className="ob-gate-txt">
            <h4>One thing before your report</h4>
            <p>Let us know about pain — or that you're pain-free — back in step 4. It's the one answer
              the analysis really needs. {signup
                ? 'You can still create your account now and finish this after.'
                : 'You can still upload your video now and finish this after.'}</p>
          </div>
        </div>
      ) : signedIn ? (
        <div className="ob-gate ok">
          <Icons.Check />
          <div className="ob-gate-txt">
            <h4>You're set for analysis</h4>
            <p>That's everything the fit report needs, and it's already saved to your profile. Next you'll
              film two short clips — about two minutes on the trainer.</p>
          </div>
        </div>
      ) : null}

      {signup ? <SignupCard signup={signup} /> : null}
    </div>
  );
}

function DoneStep({ profile, onUpload, accountCreated }: {
  profile: Profile;
  onUpload: () => void;
  /** True when this screen was reached by creating an account on the review step. */
  accountCreated: boolean;
}) {
  const ok = painAnswered(profile);
  return (
    <div className="ob-step-enter ob-done">
      <svg className="ob-done-ring" viewBox="0 0 72 72" fill="none">
        <circle cx="36" cy="36" r="33" stroke="rgba(16,185,129,0.18)" strokeWidth={3} />
        <circle cx="36" cy="36" r="33" stroke="var(--ob-accent)" strokeWidth={3} strokeLinecap="round"
                strokeDasharray="207" strokeDashoffset="40" transform="rotate(-90 36 36)" />
        <path d="M24 37l8 8 16-17" stroke="var(--ob-accent)" strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <h2>{accountCreated ? "Your account's ready." : "Your profile's saved."}</h2>
      <p>{ok
        ? "Everything's ready for analysis. Let's get your fit video — two short clips and your report is on its way."
        : "Saved and waiting. Add your pain answer whenever you're ready — you can still record your fit video now."}</p>
      <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
        <button type="button" className="ob-btn ob-btn-primary ob-btn-lg" onClick={onUpload}>
          Upload your fit video <Icons.ChevR width={18} height={18} />
        </button>
      </div>
      <p style={{ fontSize: 12, color: 'var(--bk-fg-4)', marginTop: 4 }}>Everything you entered is now in profile · settings.</p>
    </div>
  );
}

// ── Step config ────────────────────────────────────────────────────────────────

type StepKey = 'welcome' | 'bike' | 'ride' | 'body' | 'pain' | 'summary';
const STEPS: Array<{ key: StepKey; label: string; dot: boolean }> = [
  { key: 'welcome', label: 'Start',  dot: false },
  { key: 'bike',    label: 'Bike',   dot: true },
  { key: 'ride',    label: 'Riding', dot: true },
  { key: 'body',    label: 'Body',   dot: true },
  { key: 'pain',    label: 'Pain',   dot: true },
  { key: 'summary', label: 'Review', dot: true },
];

interface ResumeSnapshot { profile: Profile; stepKey: StepKey; done: boolean }

function loadResume(): ResumeSnapshot | null {
  try {
    const raw = localStorage.getItem(RESUME_KEY);
    return raw ? (JSON.parse(raw) as ResumeSnapshot) : null;
  } catch { return null; }
}

// ── Main component ───────────────────────────────────────────────────────────

const Onboarding: React.FC<OnboardingProps> = ({
  onComplete, onSave, initialValues, identity, signedIn = false, onCreateAccount, socialSignup,
}) => {
  const initialFromValues = useRef<Profile>(seedFromValues(initialValues));
  const seededFromProps = useRef(!!initialValues);
  const hydrated = useRef(false);

  const [profile, setProfile] = useState<Profile>(() => {
    const saved = loadResume();
    return saved ? { ...DEFAULT_PROFILE, ...saved.profile } : initialFromValues.current;
  });
  const [stepIdx, setStepIdx] = useState<number>(() => {
    const saved = loadResume();
    if (saved && !saved.done) {
      const idx = STEPS.findIndex((s) => s.key === saved.stepKey);
      return Math.max(0, idx);
    }
    return 0;
  });
  const [done, setDone] = useState<boolean>(() => loadResume()?.done ?? false);
  const [resumed, setResumed] = useState<boolean>(() => {
    const saved = loadResume();
    return !!(saved && !saved.done && STEPS.findIndex((s) => s.key === saved.stepKey) > 0);
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Review-step account creation (signed-out riders). Never prefilled — there is
  // no authenticated identity yet — and never written to the resume snapshot.
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupErrors, setSignupErrors] = useState<SignupErrors>({});
  const [accountCreated, setAccountCreated] = useState(false);

  // Seed from initialValues once it arrives (signed-in backend fetch), when the
  // user hasn't resumed a local snapshot and we hadn't already seeded from props.
  useEffect(() => {
    if (initialValues && !seededFromProps.current && !loadResume()) {
      seededFromProps.current = true;
      setProfile(seedFromValues(initialValues));
    }
  }, [initialValues]);

  // Mark hydration complete after first paint so we don't persist prematurely.
  useEffect(() => { hydrated.current = true; }, []);

  // Partial save (resume) on every change.
  useEffect(() => {
    if (!hydrated.current) return;
    const stepKey = STEPS[stepIdx]?.key ?? 'welcome';
    try { localStorage.setItem(RESUME_KEY, JSON.stringify({ profile, stepKey, done })); } catch { /* quota */ }
  }, [profile, stepIdx, done]);

  const set: SetFn = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const cur = STEPS[stepIdx] ?? STEPS[0];

  const canContinue = (() => {
    if (cur.key === 'bike') return !!profile.bike;
    if (cur.key === 'ride') return !!profile.position && !!profile.experience;
    if (cur.key === 'pain') return painAnswered(profile);
    return true;
  })();

  /** On the review step a signed-out rider creates an account instead of continuing. */
  const signupMode = cur.key === 'summary' && !signedIn && !!onCreateAccount;

  const goTo = (i: number) => {
    setDone(false);
    setStepIdx(Math.max(0, Math.min(STEPS.length - 1, i)));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const back = () => { if (done) { setDone(false); return; } goTo(stepIdx - 1); };

  const next = async () => {
    setResumed(false);
    if (cur.key === 'summary') {
      // Persist the profile, then show the completion screen.
      const values = buildValues(profile);
      setSaveError(null);

      // Signed out: create the account first — it also attaches the profile.
      if (signupMode && onCreateAccount) {
        const errs = validateSignup(signupEmail, signupPassword);
        setSignupErrors(errs);
        if (errs.email || errs.password) return;
        setSaving(true);
        try {
          await onCreateAccount({ email: signupEmail.trim(), password: signupPassword }, values);
        } catch (err) {
          setSignupErrors({
            form: err instanceof Error && err.message
              ? err.message
              : 'Account could not be created. Please try again.',
          });
          return;
        } finally {
          setSaving(false);
        }
        setSignupPassword('');
        setAccountCreated(true);
        setDone(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      if (onSave) {
        setSaving(true);
        try { await onSave(values); }
        catch { setSaveError('Profile could not be saved, but you can continue.'); }
        finally { setSaving(false); }
      }
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    goTo(stepIdx + 1);
  };

  const reset = () => {
    try { localStorage.removeItem(RESUME_KEY); } catch { /* ignore */ }
    setProfile({ ...DEFAULT_PROFILE });
    setStepIdx(0);
    setDone(false);
    setResumed(false);
    setAccountCreated(false);
    setSignupEmail('');
    setSignupPassword('');
    setSignupErrors({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const finishToUpload = () => {
    try { localStorage.removeItem(RESUME_KEY); } catch { /* ignore */ }
    onComplete(buildValues(profile));
  };

  const ctaLabel = cur.key === 'welcome' ? "Let's go"
    : cur.key === 'summary' ? (signupMode ? 'Create account' : 'Continue to video upload')
    : 'Continue';

  /**
   * Hand the answers to the caller before a social redirect. Unlike the email
   * path (where `onCreateAccount` carries the values across), social auth
   * unloads the page, so this is the last chance to write them down.
   */
  const persistAnswers = async () => {
    if (!onSave) return;
    await onSave(buildValues(profile));
  };

  const signupBinding: SignupBinding | null = signupMode ? {
    email: signupEmail,
    password: signupPassword,
    errors: signupErrors,
    submitting: saving,
    social: socialSignup ? socialSignup(persistAnswers) : null,
    onEmail: (v) => { setSignupEmail(v); if (signupErrors.email || signupErrors.form) setSignupErrors((e) => ({ ...e, email: undefined, form: undefined })); },
    onPassword: (v) => { setSignupPassword(v); if (signupErrors.password || signupErrors.form) setSignupErrors((e) => ({ ...e, password: undefined, form: undefined })); },
  } : null;

  const dotSteps = STEPS.filter((s) => s.dot);
  const curDot = dotSteps.findIndex((s) => s.key === cur.key);
  const dotNumber = curDot >= 0 ? curDot : (cur.key === 'welcome' ? -1 : 0);

  const goToKey = (key: StepKey) => { const i = STEPS.findIndex((s) => s.key === key); if (i >= 0) goTo(i); };

  const firstName = (identity?.name || '').split(' ')[0];

  return (
    <div className="ob-stage">
      <div className="ob-glows" aria-hidden="true">
        <div className="ob-glow ob-glow-indigo" />
        <div className="ob-glow ob-glow-accent" />
      </div>
      <div className="ob-grid" aria-hidden="true" />

      <main className="ob-main">
        {resumed && !done ? (
          <div className="ob-resume">
            <Icons.Refresh width={18} height={18} />
            <p>Welcome back{firstName ? <>, <b>{firstName}</b></> : null} — we saved your progress and picked up right where you left off.</p>
            <button type="button" className="ob-skip" onClick={reset}>Start over</button>
          </div>
        ) : null}

        <div className="ob-col">
          {!done && stepIdx > 0 ? (
            <div className="ob-toolbar">
              <button type="button" className="ob-exit" onClick={reset} title="Clears saved progress">
                <Icons.Refresh width={15} height={15} /> <span>Start over</span>
              </button>
            </div>
          ) : null}

          {!done && dotNumber >= 0 ? (
            <div className="ob-progress">
              <div className="ob-progress-top">
                <span className="ob-progress-label">{dotSteps[curDot]?.label ?? ''}</span>
                <span className="ob-progress-count">Step {curDot + 1} of {dotSteps.length}</span>
              </div>
              <div>
                <div className="ob-dots">
                  {dotSteps.map((s, i) => (
                    <div className="ob-dot-wrap" key={s.key}>
                      <button type="button"
                              className={'ob-dot' + (i < curDot ? ' done' : i === curDot ? ' active' : '')}
                              onClick={() => i <= curDot && goToKey(s.key)}
                              style={{ cursor: i <= curDot ? 'pointer' : 'default' }}>
                        {i < curDot ? <Icons.Check width={13} height={13} /> : i + 1}
                      </button>
                      {i < dotSteps.length - 1 ? <span className={'ob-dot-line' + (i < curDot ? ' done' : '')} /> : null}
                    </div>
                  ))}
                </div>
                <div className="ob-dot-labels">
                  {dotSteps.map((s, i) => (
                    <span key={s.key} className={'ob-dot-label' + (i === curDot ? ' active' : '')}>{s.label}</span>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          <div className="ob-card">
            {done ? <DoneStep profile={profile} onUpload={finishToUpload} accountCreated={accountCreated} />
              : cur.key === 'welcome' ? <WelcomeStep identity={identity} />
              : cur.key === 'bike'    ? <BikeStep profile={profile} set={set} />
              : cur.key === 'ride'    ? <RideStep profile={profile} set={set} />
              : cur.key === 'body'    ? <BodyStep profile={profile} set={set} />
              : cur.key === 'pain'    ? <PainStep profile={profile} set={set} />
              : cur.key === 'summary' ? <SummaryStep profile={profile} goTo={goTo} signedIn={signedIn} signup={signupBinding} />
              : null}
          </div>

          {!done ? (
            <div className="ob-nav">
              {stepIdx > 0 ? (
                <button type="button" className="ob-btn ob-btn-ghost" onClick={back}>
                  <Icons.ChevL width={18} height={18} /> Back
                </button>
              ) : <span />}
              <div className="ob-nav-spacer" />
              {cur.key === 'body' ? <button type="button" className="ob-skip" onClick={() => goTo(stepIdx + 1)}>Skip for now</button> : null}
              {cur.key === 'pain' && !canContinue ? (
                <span className="ob-nav-hint">Pick one — or tell us you're pain-free</span>
              ) : null}
              {signupMode ? (
                <span className="ob-nav-hint">So you don't re-enter this next time — stays on Bikendure</span>
              ) : null}
              <button type="button"
                      className={'ob-btn ob-btn-primary' + (cur.key === 'summary' ? ' ob-btn-lg' : '')}
                      onClick={next} disabled={!canContinue || saving}>
                {saving ? (signupMode ? 'Creating account…' : 'Saving…') : ctaLabel}
                {!saving ? <Icons.ChevR width={18} height={18} /> : null}
              </button>
            </div>
          ) : null}

          {saveError ? <p className="ob-save-error">{saveError}</p> : null}
        </div>
      </main>
    </div>
  );
};

export default Onboarding;
